<script setup lang="ts">
import { useIntersectionObserver } from "@vueuse/core";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-vue-next";
import { ref, defineModel, watch } from "vue";

interface Props {
  countries: {
    id: number;
    created_at: string;
    updated_at: string;
    title: string;
  }[];
  triggerClass?: string;
}
defineProps<Props>();
const emit = defineEmits<{
  (e: "handleLoadMoreCountry"): void;
}>();

const open = ref(false);
const search = defineModel("search");
const countryValue = defineModel("country");

const target = ref(null);
const targetIsVisible = ref(false);
watch(
  () => open.value,
  () => {
    if (!open.value) {
      search.value = "";
    }
  },
);
useIntersectionObserver(target, ([{ isIntersecting }]) => {
  targetIsVisible.value = isIntersecting;
  if (isIntersecting) {
    emit("handleLoadMoreCountry");
  }
});
</script>

<template>
  <div class="!relative !w-full">
    <Popover v-model:open="open" class="!w-full">
      <PopoverTrigger class="w-full">
        <Button
          variant="outline"
          role="combobox"
          :aria-expanded="open"
          :class="triggerClass"
          class="h-12 !w-full justify-between rounded-xl border-0 shadow-none bg-gray-100 py-2.5 px-4"
        >
          <span class="flex-y-center text-gray font-semibold">
            <i
              class="icon-global text-xl text-gray-450 inline-flex items-center mr-3 leading-130"
            />
            {{
              countryValue
                ? countries.find((country) => country.id === countryValue)
                    ?.title
                : $t("choose_country_and_region")
            }}
          </span>
          <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent class="popover-content-width-full p-0">
        <Command class="max-h-[260px]">
          <div class="flex items-center border-b px-3">
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="mr-2 h-4 w-4 shrink-0 opacity-50"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M10 6.5C10 8.433 8.433 10 6.5 10C4.567 10 3 8.433 3 6.5C3 4.567 4.567 3 6.5 3C8.433 3 10 4.567 10 6.5ZM9.30884 10.0159C8.53901 10.6318 7.56251 11 6.5 11C4.01472 11 2 8.98528 2 6.5C2 4.01472 4.01472 2 6.5 2C8.98528 2 11 4.01472 11 6.5C11 7.56251 10.6318 8.53901 10.0159 9.30884L12.8536 12.1464C13.0488 12.3417 13.0488 12.6583 12.8536 12.8536C12.6583 13.0488 12.3417 13.0488 12.1464 12.8536L9.30884 10.0159Z"
                fill="currentColor"
              ></path></svg
            ><input
              v-model.trim="search"
              type="text"
              aria-expanded="true"
              aria-controls="radix-vue-combobox-content-v-1"
              aria-disabled="false"
              aria-autocomplete="list"
              role="combobox"
              autocomplete="false"
              placeholder="Enter your country"
              class="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-slate-500 disabled:cursor-not-allowed disabled:opacity-50 dark:placeholder:text-slate-400"
              value="a"
              aria-activedescendant="radix-vue-combobox-option-v-4"
            />
          </div>
          <CommandEmpty>{{ $t("no_results") }}</CommandEmpty>
          <CommandList>
            <CommandGroup>
              <CommandItem
                v-for="(country, idx) in countries"
                :key="idx"
                :value="country.id"
                @select="
                  (ev) => {
                    if (typeof ev.detail.value === 'number') {
                      countryValue = ev.detail.value;
                    }
                    open = false;
                  }
                "
              >
                {{ country.title }}
                <Check
                  :class="
                    cn(
                      'ml-auto h-4 w-4',
                      countryValue === country.id ? 'opacity-100' : 'opacity-0',
                    )
                  "
                />
              </CommandItem>
              <div ref="target" class="py-0.5 w-full"></div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  </div>
</template>
